import { getInformationWithData, getInformationWithDataV3 } from './CABE';
import { getValueCookie } from './cookieService';

/**
 * goalsService — única puerta de lectura/escritura de las metas de venta (demo 2026-11-04).
 *
 * Una meta es un total esperado para un vendedor o un customer entre dos fechas.
 *
 * ESTADO ACTUAL
 *  - Metas: SIN backend. Se guardan en localStorage de este navegador (MOCK).
 *  - Mayaland (3) y Altitude (6): customers, vendedores y facturas vienen de endpoints reales
 *    de solo lectura, así que el avance se calcula con ventas reales.
 *  - Nebraska (7), o si el catálogo real falla: datos MOCK marcados como tales. Sus mirrors
 *    están vacíos y los endpoints caen en las tablas de Mayaland, así que nunca se consultan.
 *
 * PARA CONECTAR EL BACKEND: buscar "TODO: BACKEND", reemplazar el cuerpo de cada función y
 * borrar lo marcado "MOCK". Las pantallas solo usan las funciones exportadas.
 *
 * Forma de una meta (la misma que debería devolver el backend):
 *   {
 *     id: string,
 *     goalType: 'salesperson' | 'customer',
 *     targetNo: string,            // SalespersonNo o CustomerNo
 *     targetAmount: number,
 *     startDate: 'YYYY-MM-DD',
 *     endDate: 'YYYY-MM-DD',
 *     createdBy: string,
 *     createdAt: string ISO,
 *   }
 *
 * Reglas de negocio del avance (el backend debe repetirlas):
 *  - Cuentan las facturas S/O, IN y CM (los CM ya vienen negativos), por TransactionDate.
 *  - Monto: sin flete ni impuestos.
 *  - Meta de vendedor: SalespersonNo de la factura, no el vendedor actual del customer.
 *  - Meta de customer: CustomerNo, sin importar quién vendió. Una venta cuenta en ambas.
 *  - El avance nunca se guarda, siempre se calcula.
 */

const COMPANIES_WITH_REAL_DATA = [3, 6];
const NON_ASSIGNABLE_SALESPEOPLE = ['', 'TOT', '0000'];

export const GOAL_TYPE = { salesperson: 'salesperson', customer: 'customer' };
export const GOAL_STATUS = {
    upcoming: 'upcoming',
    inProgress: 'inProgress',
    achieved: 'achieved',
    missed: 'missed',
    noData: 'noData',
};

export const GOAL_AMOUNT_MAX = 999999999.99;
const GOAL_AMOUNT_MAX_INTEGER_DIGITS = 9;
export const GOAL_DATE_MIN = '2020-01-01';
export const GOAL_DATE_MAX = '2099-12-31';
export const DATA_SOURCE = { real: 'real', mock: 'mock', unavailable: 'unavailable' };

const CACHE_MS = 5 * 60 * 1000;
const HISTORY_DAYS = 365;

const str = (v) => (v === undefined || v === null ? '' : String(v).trim());
const compareText = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
const currentUserName = () => str(getValueCookie('userName')).toLowerCase();

export const getCurrentCompanyId = () => (
    getValueCookie('SubCompanyId') ? Number(getValueCookie('SubCompanyId')) : Number(getValueCookie('CompanyId'))
);

// ─── Formato y fechas ────────────────────────────────────────────────────────

const moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const formatMoney = (amount) => moneyFormat.format(Number(amount) || 0);

// Las fechas viajan como 'YYYY-MM-DD' y se comparan como texto. Nunca pasan por new Date(texto),
// que las interpreta en UTC y en Estados Unidos las movería un día.
const pad = (n) => String(n).padStart(2, '0');
const toIsoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const fromIsoDate = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const getToday = () => toIsoDate(new Date());

const addDays = (iso, days) => {
    const date = fromIsoDate(iso);
    date.setDate(date.getDate() + days);
    return toIsoDate(date);
};

const monthBounds = (iso, monthOffset) => {
    const date = fromIsoDate(iso);
    const first = new Date(date.getFullYear(), date.getMonth() + monthOffset, 1);
    const last = new Date(date.getFullYear(), date.getMonth() + monthOffset + 1, 0);
    return { startDate: toIsoDate(first), endDate: toIsoDate(last) };
};

// Noon evita que un cambio de horario deje la diferencia en 23 o 25 horas.
const daysBetween = (startIso, endIso) => {
    const start = fromIsoDate(startIso);
    const end = fromIsoDate(endIso);
    start.setHours(12);
    end.setHours(12);
    return Math.round((end - start) / 86400000);
};

export const formatDate = (iso) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    return `${month}/${day}/${year}`;
};

export const formatDateRange = (startDate, endDate) => `${formatDate(startDate)} – ${formatDate(endDate)}`;

const isValidIsoDate = (iso) => /^\d{4}-\d{2}-\d{2}$/.test(iso) && toIsoDate(fromIsoDate(iso)) === iso;

// ─── Validación ──────────────────────────────────────────────────────────────

export const parseAmount = (text) => {
    const clean = str(text).replace(/[$,\s]/g, '');
    return /^(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(clean) ? Number(clean) : NaN;
};

// Lo que el campo de monto deja escribir o pegar: solo dígitos y un punto, hasta 2 decimales.
export const sanitizeAmountInput = (text) => {
    const [integers, ...decimals] = String(text).replace(/[^\d.]/g, '').split('.');
    const whole = integers.slice(0, GOAL_AMOUNT_MAX_INTEGER_DIGITS);
    return decimals.length > 0 ? `${whole}.${decimals.join('').slice(0, 2)}` : whole;
};

// Al salir del campo: "007" o "5." quedan como "7.00" y "5.00". Lo inválido se deja para que se vea el error.
export const normalizeAmountInput = (text) => {
    const amount = parseAmount(text);
    return Number.isNaN(amount) ? text : amount.toFixed(2);
};

const validateAmount = (targetAmount) => {
    const amount = parseAmount(targetAmount);
    if (!str(targetAmount)) return 'Enter the expected total.';
    if (Number.isNaN(amount)) return 'Enter numbers only, with up to 2 decimals.';
    if (amount <= 0) return 'The expected total must be greater than zero.';
    if (amount > GOAL_AMOUNT_MAX) return `Use ${formatMoney(GOAL_AMOUNT_MAX)} or less.`;
    return undefined;
};

const validateDate = (date, emptyMessage) => {
    if (!str(date)) return emptyMessage;
    if (!isValidIsoDate(date)) return 'Enter a valid date.';
    if (date < GOAL_DATE_MIN || date > GOAL_DATE_MAX) {
        return `Use a date between ${formatDate(GOAL_DATE_MIN)} and ${formatDate(GOAL_DATE_MAX)}.`;
    }
    return undefined;
};

// Mientras se escribe el año, el input entrega 0002, 0020, 0202... Estos todavía no merecen un error.
export const isDateStillBeingTyped = (date) => !str(date) || date < GOAL_DATE_MIN;

// Devuelve { startDate?, endDate? }. Sin targetNo no se puede revisar el traslape.
export const validateGoalDates = ({ goalType, targetNo, startDate, endDate }, existingGoals) => {
    const errors = {};
    const startError = validateDate(startDate, 'Enter the start date.');
    const endError = validateDate(endDate, 'Enter the end date.');
    if (startError) errors.startDate = startError;
    if (endError) errors.endDate = endError;
    if (startError || endError) return errors;

    if (endDate < startDate) {
        errors.endDate = 'The end date cannot be before the start date.';
        return errors;
    }
    const overlapping = str(targetNo) && existingGoals.find((g) => (
        g.goalType === goalType && g.targetNo === targetNo && g.startDate <= endDate && g.endDate >= startDate
    ));
    if (overlapping) {
        errors.startDate = `These dates overlap an existing goal (${formatDateRange(overlapping.startDate, overlapping.endDate)}).`;
    }
    return errors;
};

// Devuelve { targetNo?, targetAmount?, startDate?, endDate? } con el mensaje de cada campo inválido.
// existingGoals: las metas vigentes de la compañía; no se permiten traslapes para el mismo objetivo.
// TODO: BACKEND — create debe repetir estas reglas y responder 400
//   { status: { code: 0 }, message, fieldErrors: { targetNo?, targetAmount?, startDate?, endDate? } }.
export const validateGoal = (fields, existingGoals) => {
    const errors = validateGoalDates(fields, existingGoals);
    if (!str(fields.targetNo)) {
        errors.targetNo = fields.goalType === GOAL_TYPE.customer ? 'Choose a customer.' : 'Choose a salesperson.';
    }
    const amountError = validateAmount(fields.targetAmount);
    if (amountError) errors.targetAmount = amountError;
    return errors;
};

// ─── Cálculo de avance ───────────────────────────────────────────────────────

// Agrupa una sola vez, para no recorrer miles de facturas por cada meta.
export const indexInvoices = (invoices) => {
    const customer = new Map();
    const salesperson = new Map();
    const add = (map, key, invoice) => {
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(invoice);
    };
    invoices.forEach((invoice) => {
        add(customer, invoice.customerNo, invoice);
        add(salesperson, invoice.salespersonNo, invoice);
    });
    return { customer, salesperson };
};

const roundMoney = (amount) => Math.round(amount * 100) / 100;

const goalStatus = (goal, achieved, today, salesAvailable) => {
    if (today < goal.startDate) return GOAL_STATUS.upcoming;
    if (!salesAvailable) return GOAL_STATUS.noData;
    if (achieved >= goal.targetAmount) return GOAL_STATUS.achieved;
    return today > goal.endDate ? GOAL_STATUS.missed : GOAL_STATUS.inProgress;
};

// salesIndex null = no se pudieron cargar las facturas; el avance queda sin dato, no en cero.
export const computeProgress = (goal, salesIndex, today) => {
    const salesAvailable = Boolean(salesIndex);
    let achieved = 0;
    if (salesAvailable) {
        (salesIndex[goal.goalType].get(goal.targetNo) || []).forEach((invoice) => {
            if (invoice.transactionDate >= goal.startDate && invoice.transactionDate <= goal.endDate) {
                achieved += invoice.amount;
            }
        });
    }
    achieved = roundMoney(achieved);
    return {
        ...goal,
        achieved: salesAvailable ? achieved : null,
        remaining: salesAvailable ? roundMoney(Math.max(goal.targetAmount - achieved, 0)) : null,
        percent: salesAvailable ? (achieved / goal.targetAmount) * 100 : null,
        daysLeft: today > goal.endDate ? 0 : daysBetween(today < goal.startDate ? goal.startDate : today, goal.endDate) + 1,
        status: goalStatus(goal, achieved, today, salesAvailable),
    };
};

// La meta de "hoy": la que está corriendo o, si no hay, la próxima en empezar.
export const pickCurrentGoal = (targetGoals, today) => {
    const running = targetGoals.find((g) => g.startDate <= today && g.endDate >= today);
    if (running) return running;
    return targetGoals
        .filter((g) => g.startDate > today)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))[0] || null;
};

export const sortGoalsNewestFirst = (goals) => [...goals].sort((a, b) => (
    b.startDate.localeCompare(a.startDate) || b.createdAt.localeCompare(a.createdAt)
));

// Ventas y última venta de cada customer en los últimos 12 meses. Se recorta porque las facturas
// cargadas pueden cubrir más, si alguna meta empezó antes.
export const summarizeCustomerSales = (invoices, today) => {
    const from = addDays(today, -HISTORY_DAYS);
    const byCustomer = new Map();
    invoices.forEach((invoice) => {
        if (invoice.transactionDate < from) return;
        const entry = byCustomer.get(invoice.customerNo) || { sales: 0, lastSale: '' };
        entry.sales += invoice.amount;
        if (invoice.transactionDate > entry.lastSale) entry.lastSale = invoice.transactionDate;
        byCustomer.set(invoice.customerNo, entry);
    });
    return byCustomer;
};

export const groupGoalsByTarget = (goals) => {
    const grouped = new Map();
    goals.forEach((g) => {
        if (!grouped.has(g.targetNo)) grouped.set(g.targetNo, []);
        grouped.get(g.targetNo).push(g);
    });
    return grouped;
};

// Precarga del formulario: el monto de la última meta, el mismo número de días y empezando al
// día siguiente de su fin. Copiar las mismas fechas chocaría con la regla de no traslapar.
// TODO: BACKEND — POST {apiPrefix}/v3/goals/last  body { data: { idCompany, goalType, targetNo } }
//   o devolverla en list; este cálculo puede quedarse en el front.
export const buildGoalDefaults = (goalType, targetNo, goals, today) => {
    const last = goals
        .filter((g) => g.goalType === goalType && g.targetNo === targetNo)
        .sort((a, b) => b.endDate.localeCompare(a.endDate))[0];

    if (!last) {
        return { targetAmount: '', startDate: today, endDate: monthBounds(today, 0).endDate, lastGoal: null };
    }
    const startDate = addDays(last.endDate, 1);
    return {
        targetAmount: last.targetAmount.toFixed(2),
        startDate,
        endDate: addDays(startDate, daysBetween(last.startDate, last.endDate)),
        lastGoal: last,
    };
};

// ─── Catálogos y facturas (lectura real) ─────────────────────────────────────

const withSearchText = (c) => ({
    ...c,
    searchText: [c.customerNo, c.name, c.address, c.city, c.state, c.zipCode, c.phone, c.salespersonNo].join(' ').toLowerCase(),
});

const normalizeCustomers = (rows) => {
    const byNo = new Map();
    rows.forEach((r) => {
        const customerNo = str(r.CustomerNo);
        if (!customerNo || byNo.has(customerNo)) return;
        byNo.set(customerNo, withSearchText({
            customerNo,
            name: str(r.CustomerName) || customerNo,
            address: str(r.AddressLine1),
            city: str(r.City),
            state: str(r.State),
            zipCode: str(r.ZipCode),
            phone: str(r.TelephoneNo),
            salespersonNo: str(r.SalespersonNo),
        }));
    });
    return Array.from(byNo.values());
};

const normalizeInvoices = (rows) => rows.map((r) => ({
    invoiceNo: str(r.InvoiceNo),
    invoiceType: str(r.InvoiceType),
    customerNo: str(r.CustomerNo),
    salespersonNo: str(r.SalespersonNo),
    transactionDate: str(r.TransactionDate),
    amount: Number(r.NonTaxableSalesAmt) || 0,
}));

// Une los usuarios del dashboard (traen nombre) con los códigos que aparecen en las facturas:
// dashboardSalesperson solo trae inDashboard = 1 y omite vendedores que sí facturan.
const normalizeSalespeople = (dashboardRows, invoices) => {
    const byNo = new Map();
    dashboardRows.forEach((u) => {
        const salespersonNo = str(u.salesPerson);
        if (NON_ASSIGNABLE_SALESPEOPLE.includes(salespersonNo) || byNo.has(salespersonNo)) return;
        byNo.set(salespersonNo, { salespersonNo, name: str(u.userName) || salespersonNo });
    });
    invoices.forEach(({ salespersonNo }) => {
        if (NON_ASSIGNABLE_SALESPEOPLE.includes(salespersonNo) || byNo.has(salespersonNo)) return;
        byNo.set(salespersonNo, { salespersonNo, name: salespersonNo });
    });
    return Array.from(byNo.values()).sort((a, b) => compareText(a.name, b.name));
};

export const salespersonLabel = (salesperson) => {
    if (!salesperson) return '';
    return salesperson.name && salesperson.name !== salesperson.salespersonNo
        ? `${salesperson.name} (${salesperson.salespersonNo})`
        : salesperson.salespersonNo;
};

const isSuccess = (resp) => Boolean(resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data));

const fetchRealCustomers = async (idCompany) => {
    const resp = await getInformationWithDataV3('/v3/salesorder/offline-customers', {
        idCompany,
        salespersonNo: getValueCookie('SalesPerson'),
        idTypeofUser: getValueCookie('TypeUser'),
    });
    return isSuccess(resp) ? normalizeCustomers(resp.data) : [];
};

// El controlador lee companyid en minúsculas; se mandan las dos formas.
const fetchDashboardSalespeople = async (idCompany) => {
    const resp = await getInformationWithData('/v2/company/dashboardSalesperson', { companyId: idCompany, companyid: idCompany });
    return isSuccess(resp) ? resp.data : [];
};

// Devuelve null si falló, para distinguir "sin ventas" de "no se pudo consultar".
// TODO: BACKEND — el avance debería calcularlo el back con un SUM por meta en lugar de bajar un
//   año de facturas. Además debe sumar TaxableSalesAmt, que este endpoint no devuelve (hoy es 0).
const fetchRealInvoices = async (idCompany, from, to) => {
    const resp = await getInformationWithDataV3('/v3/sales/total-by-route-txn', {
        idCompany,
        SalespersonNo: 'TOT',
        Start: from,
        End: to,
    });
    return isSuccess(resp) ? normalizeInvoices(resp.data) : null;
};

// ─── MOCK: catálogos y facturas de ejemplo (borrar al conectar Nebraska) ─────

const MOCK_SALESPEOPLE = [
    { salespersonNo: 'MK01', name: 'Sample Rep A' },
    { salespersonNo: 'MK02', name: 'Sample Rep B' },
    { salespersonNo: 'MK03', name: 'Sample Rep C' },
];
const MOCK_NAME_A = ['Maya', 'Sunrise', 'La Esperanza', 'El Sol', 'Golden', 'Fresh', 'Plaza', 'Central'];
const MOCK_NAME_B = ['Market', 'Grocery', 'Foods', 'Supermarket', 'Mini Mart', 'Produce'];
const MOCK_PLACES = [['Omaha', 'NE'], ['Lincoln', 'NE'], ['Grand Island', 'NE'], ['Kearney', 'NE']];
const MOCK_CUSTOMER_COUNT = 240;
const MOCK_CUSTOMERS_WITH_SALES = 90;

const buildMockCustomers = () => {
    const rows = [];
    for (let i = 1; i <= MOCK_CUSTOMER_COUNT; i += 1) {
        const place = MOCK_PLACES[i % MOCK_PLACES.length];
        rows.push({
            CustomerNo: `MOCK${String(i).padStart(4, '0')}`,
            CustomerName: `[SAMPLE] ${MOCK_NAME_A[i % MOCK_NAME_A.length]} ${MOCK_NAME_B[i % MOCK_NAME_B.length]} ${i}`,
            AddressLine1: `${100 + i} Sample St`,
            City: place[0],
            State: place[1],
            ZipCode: String(10000 + i),
            TelephoneNo: `555-${String(1000 + i)}`,
            SalespersonNo: MOCK_SALESPEOPLE[i % MOCK_SALESPEOPLE.length].salespersonNo,
        });
    }
    return normalizeCustomers(rows);
};

// Una factura cada 5 días por customer; salen del índice para no cambiar al recargar.
const buildMockInvoices = (customers, from, to) => {
    const invoices = [];
    customers.slice(0, MOCK_CUSTOMERS_WITH_SALES).forEach((c, i) => {
        let day = addDays(from, i % 5);
        let n = 0;
        while (day <= to) {
            n += 1;
            const isCredit = n % 11 === 0;
            const base = 150 + ((i * 37 + n * 53) % 900);
            invoices.push({
                invoiceNo: `MOCK-${i}-${n}`,
                invoiceType: isCredit ? 'CM' : 'IN',
                customerNo: c.customerNo,
                salespersonNo: c.salespersonNo,
                transactionDate: day,
                amount: isCredit ? -Math.round(base / 4) : base,
            });
            day = addDays(day, 5);
        }
    });
    return invoices;
};

// ─── MOCK: almacenamiento de metas en localStorage ───────────────────────────

// Las metas sobre customers de ejemplo no se mezclan con las de customers reales.
const mockStorageKey = (idCompany, scope) => `goals:demo:v1:${idCompany}:${scope}`;
const mockDelay = () => new Promise((resolve) => setTimeout(resolve, 150));

const isUsableGoal = (g) => Boolean(g && g.id && g.goalType && g.targetNo && g.startDate && g.endDate && Number(g.targetAmount) > 0);

const mockReadAll = (idCompany, scope) => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(mockStorageKey(idCompany, scope)));
        return Array.isArray(parsed) ? parsed.filter(isUsableGoal) : [];
    } catch (e) {
        return [];
    }
};

const mockWriteAll = (idCompany, scope, goals) => {
    try {
        window.localStorage.setItem(mockStorageKey(idCompany, scope), JSON.stringify(goals));
    } catch (e) {
        throw new Error('This browser could not save the goal.');
    }
};

const roundToHundreds = (amount) => Math.max(Math.round(amount / 100) * 100, 500);

const salesInRange = (rows, { startDate, endDate }) => rows.reduce((total, invoice) => (
    invoice.transactionDate >= startDate && invoice.transactionDate <= endDate ? total + invoice.amount : total
), 0);

// Primera visita: metas de ejemplo sobre los que más venden, para que la demo no se vea vacía.
// Los montos son inventados, pero salen de sus ventas para que haya alcanzadas y no alcanzadas.
const mockSeedGoalsIfNeeded = (idCompany, scope, customers, salespeople, invoices, today) => {
    // Sin facturas no se siembra: se reintenta en la siguiente visita, con ventas de las que salir.
    if (!invoices || window.localStorage.getItem(mockStorageKey(idCompany, scope)) !== null) return;

    const index = indexInvoices(invoices);
    const topByIndex = (map, allowed, limit) => Array.from(map.entries())
        .filter(([no]) => allowed.has(no))
        .map(([no, rows]) => ({ no, total: rows.reduce((sum, r) => sum + r.amount, 0), rows }))
        .sort((a, b) => b.total - a.total)
        .slice(0, limit);

    const lastMonth = monthBounds(today, -1);
    const thisMonth = monthBounds(today, 0);
    const nextMonth = monthBounds(today, 1);
    const seeds = [];
    const addTargets = (goalType, targets) => {
        targets.forEach(({ no, rows }, i) => {
            const lastMonthSales = salesInRange(rows, lastMonth);
            // Una de cada dos queda alcanzada el mes pasado, y la otra no.
            const lastMonthFactor = i % 2 === 0 ? 0.9 : 1.2;
            seeds.push({ goalType, targetNo: no, ...lastMonth, targetAmount: roundToHundreds(lastMonthSales * lastMonthFactor) });
            seeds.push({ goalType, targetNo: no, ...thisMonth, targetAmount: roundToHundreds(lastMonthSales * 1.05) });
            if (i === 0) seeds.push({ goalType, targetNo: no, ...nextMonth, targetAmount: roundToHundreds(lastMonthSales * 1.1) });
        });
    };
    addTargets(GOAL_TYPE.salesperson, topByIndex(index.salesperson, new Set(salespeople.map((s) => s.salespersonNo)), 4));
    addTargets(GOAL_TYPE.customer, topByIndex(index.customer, new Set(customers.map((c) => c.customerNo)), 6));

    const now = Date.now();
    mockWriteAll(idCompany, scope, seeds.map((seed, i) => ({
        ...seed,
        id: `goal-sample-${i + 1}`,
        createdBy: 'sample.admin',
        createdAt: new Date(now - (seeds.length - i) * 3600000).toISOString(),
        deletedBy: '',
        deletedAt: '',
    })));
};

const activeOnly = (goals) => goals.filter((g) => !g.deletedAt);

// ─── API de la pantalla ──────────────────────────────────────────────────────

// Lo real tarda unos segundos; se conserva un rato para navegar entre pantallas sin esperar.
const readCache = new Map();
const isFresh = (entry) => entry && Date.now() - entry.loadedAt < CACHE_MS;

const loadCatalogSources = async (idCompany) => {
    const cached = readCache.get(`catalogs:${idCompany}`);
    if (isFresh(cached)) return cached;

    let customers = [];
    let dashboardRows = [];
    if (COMPANIES_WITH_REAL_DATA.includes(idCompany)) {
        [customers, dashboardRows] = await Promise.all([
            fetchRealCustomers(idCompany).catch(() => []),
            fetchDashboardSalespeople(idCompany).catch(() => []),
        ]);
    }
    const isReal = customers.length > 0;
    const entry = {
        customers: isReal ? customers : buildMockCustomers(),
        dashboardRows,
        customersSource: isReal ? DATA_SOURCE.real : DATA_SOURCE.mock,
        loadedAt: Date.now(),
    };
    if (isReal) readCache.set(`catalogs:${idCompany}`, entry);
    return entry;
};

const loadInvoices = async (idCompany, customersSource, customers, from, to) => {
    if (customersSource === DATA_SOURCE.mock) return buildMockInvoices(customers, from, to);

    const cached = readCache.get(`invoices:${idCompany}`);
    if (isFresh(cached) && cached.from <= from) return cached.invoices;

    const invoices = await fetchRealInvoices(idCompany, from, to).catch(() => null);
    if (invoices) readCache.set(`invoices:${idCompany}`, { invoices, from, loadedAt: Date.now() });
    return invoices;
};

// Carga todo lo que usan las pantallas. invoices es null si no se pudieron consultar.
// TODO: BACKEND — POST {apiPrefix}/v3/goals/list
//   body { data: { idCompany } }
//   resp { status: { code: 1 }, data: [{ ...meta, achieved, percent, status }] }
//   El usuario del token debe validarse contra userCompany.idTypeofUser = 1 (ver soMissingTool.ts);
//   la cookie TypeUser solo oculta la pantalla.
export const loadGoalsData = async (idCompany) => {
    const today = getToday();
    const { customers, dashboardRows, customersSource } = await loadCatalogSources(idCompany);
    const scope = customersSource === DATA_SOURCE.real ? 'live' : 'mock';

    const storedGoals = activeOnly(mockReadAll(idCompany, scope));
    const defaultFrom = addDays(today, -HISTORY_DAYS);
    const from = storedGoals.reduce((earliest, g) => (g.startDate < earliest ? g.startDate : earliest), defaultFrom);

    const invoices = await loadInvoices(idCompany, customersSource, customers, from, today);

    const salespeople = normalizeSalespeople(dashboardRows, invoices || []);
    let salespeopleSource = DATA_SOURCE.real;
    let finalSalespeople = salespeople;
    if (customersSource === DATA_SOURCE.mock) {
        finalSalespeople = MOCK_SALESPEOPLE;
        salespeopleSource = DATA_SOURCE.mock;
    } else if (salespeople.length === 0) {
        salespeopleSource = DATA_SOURCE.unavailable;
    }

    // MOCK: quitar al conectar las metas.
    try {
        mockSeedGoalsIfNeeded(idCompany, scope, customers, finalSalespeople, invoices, today);
    } catch (e) {
        // Sin localStorage la pantalla igual carga; el error sale al intentar guardar.
        console.error('Could not create the sample goals:', e);
    }
    await mockDelay();

    const invoiceDates = (invoices || []).map((i) => i.transactionDate).filter(Boolean);
    return {
        today,
        scope,
        customers,
        salespeople: finalSalespeople,
        invoices,
        goals: activeOnly(mockReadAll(idCompany, scope)),
        invoicesThrough: invoiceDates.length > 0 ? invoiceDates.reduce((a, b) => (a > b ? a : b)) : '',
        customersSource,
        salespeopleSource,
        salesSource: customersSource === DATA_SOURCE.mock
            ? DATA_SOURCE.mock
            : (invoices ? DATA_SOURCE.real : DATA_SOURCE.unavailable),
    };
};

// TODO: BACKEND — POST {apiPrefix}/v3/goals/create
//   body { data: { idCompany, goalType, targetNo, targetAmount, startDate, endDate } }
//   resp { status: { code: 1 }, data: meta }. created_by sale del token, no del body.
export const createGoal = async (idCompany, scope, fields) => {
    await mockDelay();
    const all = mockReadAll(idCompany, scope);
    const fieldErrors = validateGoal(fields, activeOnly(all));
    if (Object.keys(fieldErrors).length > 0) {
        const error = new Error('The goal has invalid fields.');
        error.fieldErrors = fieldErrors;
        throw error;
    }
    const now = new Date().toISOString();
    const goal = {
        id: `goal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        goalType: fields.goalType,
        targetNo: str(fields.targetNo),
        targetAmount: parseAmount(fields.targetAmount),
        startDate: fields.startDate,
        endDate: fields.endDate,
        createdBy: currentUserName(),
        createdAt: now,
        deletedBy: '',
        deletedAt: '',
    };
    mockWriteAll(idCompany, scope, [...all, goal]);
    return goal;
};

// Borrado lógico: deja de mostrarse, pero queda quién y cuándo.
// TODO: BACKEND — POST {apiPrefix}/v3/goals/delete
//   body { data: { idCompany, id } }. deleted_by sale del token. UPDATE, nunca DELETE.
export const deleteGoal = async (idCompany, scope, goalId) => {
    await mockDelay();
    const all = mockReadAll(idCompany, scope);
    const index = all.findIndex((g) => g.id === goalId && !g.deletedAt);
    if (index === -1) throw new Error('This goal no longer exists.');
    all[index] = { ...all[index], deletedBy: currentUserName(), deletedAt: new Date().toISOString() };
    mockWriteAll(idCompany, scope, all);
};
